"use client";

import { useState } from "react";
import { EmptyTasksIcon, ErrorIcon } from "@/components/icons";
import { Button } from "@/components/ui/Button";
import { TaskList } from "./TaskList";
import { TaskLogViewer } from "./TaskLogViewer";
import { useCancelTask, useExecuteTask } from "../data/task.mutations";
import { useTasks } from "../data/task.queries";
import styles from "./TaskWorkspace.module.css";

const EMPTY_TASKS = [] as const;

function LoadingState() {
  return (
    <section className={styles.statePanel} aria-live="polite" aria-busy="true">
      <div className={styles.spinner} aria-hidden="true" />
      <div>
        <h2>Loading tasks</h2>
      </div>
    </section>
  );
}

function MessageState({ variant, onRetry }: { variant: "empty" | "error"; onRetry?: () => void }) {
  const isError = variant === "error";
  const StateIcon = isError ? ErrorIcon : EmptyTasksIcon;

  return (
    <section className={styles.statePanel} role={isError ? "alert" : undefined}>
      <div className={styles.stateIcon} data-variant={variant}>
        <StateIcon />
      </div>
      <div>
        <h2>{isError ? "Unable to load tasks" : "No tasks available"}</h2>
        {isError && onRetry ? (
          <Button className={styles.retryButton} variant="secondary" size="sm" onClick={onRetry}>
            Try again
          </Button>
        ) : null}
      </div>
    </section>
  );
}

export function TaskWorkspace() {
  const tasksQuery = useTasks();
  const executeTask = useExecuteTask();
  const cancelTaskMutation = useCancelTask();
  const [selectedTaskId, setSelectedTaskId] = useState<string>();
  const [actionAnnouncement, setActionAnnouncement] = useState("");
  const [actionError, setActionError] = useState("");
  const tasks = tasksQuery.data ?? EMPTY_TASKS;
  const selectedTask = tasks.find((task) => task.id === selectedTaskId);
  const pendingTaskId = executeTask.isPending
    ? executeTask.variables
    : cancelTaskMutation.isPending
      ? cancelTaskMutation.variables
      : undefined;

  function runTask(
    taskId: string,
    callbacks?: { onSuccess?: () => void; onError?: () => void },
  ) {
    setActionError("");
    executeTask.mutate(taskId, {
      onSuccess: () => {
        setActionAnnouncement(`${taskId} started`);
        callbacks?.onSuccess?.();
      },
      onError: () => {
        setActionError(`${taskId} could not be started.`);
        callbacks?.onError?.();
      },
    });
  }

  function cancelTask(
    taskId: string,
    callbacks?: { onSuccess?: () => void; onError?: () => void },
  ) {
    setActionError("");
    cancelTaskMutation.mutate(taskId, {
      onSuccess: () => {
        setActionAnnouncement(`${taskId} stopped`);
        callbacks?.onSuccess?.();
      },
      onError: () => {
        setActionError(`${taskId} could not be stopped.`);
        callbacks?.onError?.();
      },
    });
  }

  return (
    <article className={styles.page}>
      {tasks.length === 0 ? (
        <header className={styles.pageHeader}>
          <div className={styles.titleRow}>
            <h1>Tasks</h1>
          </div>
        </header>
      ) : null}

      {tasksQuery.isPending ? <LoadingState /> : null}
      {tasksQuery.isError && tasks.length === 0 ? (
        <MessageState variant="error" onRetry={() => void tasksQuery.refetch()} />
      ) : null}
      {tasksQuery.isSuccess && tasks.length === 0 ? <MessageState variant="empty" /> : null}

      {tasks.length > 0 ? (
        <TaskList
          tasks={tasks}
          pendingTaskId={pendingTaskId}
          onViewLogs={setSelectedTaskId}
          onRunTask={runTask}
          onCancelTask={cancelTask}
        />
      ) : null}

      {selectedTask ? (
        <TaskLogViewer
          task={selectedTask}
          isActionPending={pendingTaskId === selectedTask.id}
          onRunTask={runTask}
          onCancelTask={cancelTask}
          onClose={() => setSelectedTaskId(undefined)}
        />
      ) : null}
      {actionError ? (
        <p className={styles.actionError} role="alert">
          {actionError}
        </p>
      ) : null}
      <p className={styles.srOnly} role="status" aria-live="polite">
        {actionAnnouncement}
      </p>
    </article>
  );
}
